import { IconDigitalBooks } from "@studyplus/boron-ui";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { ButtonSpinner } from "../../components/atoms/Button/ButtonSpinner";
import ContentBox from "../../components/atoms/ContentBox";
import Input from "../../components/atoms/Input";
import {
  FieldError,
  FileUploadFields,
  ONE_MB,
  StepSection,
  TEN_MB,
  validateFileSize,
} from "../../components/features/MultiSectionBatches/DrillFormComponents";
import { MultiSectionBatchesDescription } from "../../components/features/MultiSectionBatches/MultiSectionBatchesDescription";
import { useDownloadDrillTemplate } from "../../components/features/MultiSectionBatches/useDownloadDrillTemplate";
import { BreadcrumbsView } from "../../components/general/BreadcrumbsView";
import { useFetchAdminSections } from "../../hooks/http/useFetchAdminSections";
import OperatorInterface from "../../interfaces/OperatorInterface";
import { useDrillDetail } from "./useDrillDetail";
import { UpdateDrillFormValues, useUpdateDrill } from "./useUpdateDrill";

const HELP_URL =
  "https://fs-help.studyplus.co.jp/ja/articles/14648515-%E3%83%87%E3%82%B8%E3%82%BF%E3%83%AB%E6%95%99%E6%9D%90-%E3%83%89%E3%83%AA%E3%83%AB-%E9%80%B2%E5%BA%A6%E8%A1%A8-%E3%81%AE%E5%85%A5%E7%A8%BF%E6%96%B9%E6%B3%95";

const breadcrumbItems = [
  { label: "校舎横断操作", url: "/multi_section_batches" },
  {
    label: "デジタル教材の登録",
    url: "/multi_section_batches/upload_drill/list",
  },
  { label: "デジタル教材の更新" },
];

const initialValues: UpdateDrillFormValues = {
  drillName: "",
  csvFile: null,
  drillImage: null,
  contentImagesZip: null,
  contentSoundsZip: null,
  contentPdfsZip: null,
};

const validationSchema = yup.object().shape({
  drillName: yup
    .string()
    .required("教材名を入力してください")
    .max(512, "教材名は512文字以内で入力してください"),
  csvFile: yup.mixed().required("CSVファイルは必須です"),
  contentImagesZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは1MB以下にしてください",
      validateFileSize(ONE_MB),
    ),
  contentSoundsZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは10MB以下にしてください",
      validateFileSize(TEN_MB),
    ),
  contentPdfsZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは10MB以下にしてください",
      validateFileSize(TEN_MB),
    ),
});

type Props = {
  currentOperator: OperatorInterface;
};

export const UpdatePage = ({ currentOperator }: Props) => {
  const { materialCode } = useParams<{ materialCode: string }>();
  const { data, isLoading: isLoadingAdminSections } = useFetchAdminSections(
    currentOperator.id,
  );
  const hasNoAdminSections =
    !isLoadingAdminSections && (data?.sections ?? []).length === 0;
  const { drill, isLoading } = useDrillDetail(materialCode ?? "");
  const { isPending, handleSubmit } = useUpdateDrill(materialCode ?? "");
  const { downloadTemplate, isDownloading } = useDownloadDrillTemplate();

  const drillInitialValues: UpdateDrillFormValues = {
    ...initialValues,
    drillName: drill?.name ?? "",
  };

  if (isLoadingAdminSections || isLoading) {
    return (
      <div className="flex flex-col md:w-[90%]">
        <div className="py-5">
          <BreadcrumbsView items={breadcrumbItems} />
        </div>
        <ContentBox className="md:w-[80%] md:p-10">
          <p>読み込み中...</p>
        </ContentBox>
      </div>
    );
  }

  if (hasNoAdminSections) {
    return (
      <div className="flex flex-col md:w-[90%]">
        <div className="py-5">
          <BreadcrumbsView items={breadcrumbItems} />
        </div>
        <ContentBox className="md:w-[80%] md:p-10">
          <p className="text-red-400">管理者権限を持つ校舎がありません。</p>
        </ContentBox>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:w-[90%]">
      <div className="py-5">
        <BreadcrumbsView items={breadcrumbItems} />
      </div>
      <ContentBox className="md:w-[80%] md:p-10">
        <div className="flex items-center gap-6">
          <IconDigitalBooks className="text-gray-600" />
          <h2 className="text-xl">デジタル教材の更新</h2>
        </div>
        <MultiSectionBatchesDescription
          helpUrl={HELP_URL}
          isOnlyLearningManagementStudent={false}
          noteText=""
          className="!mt-[16px] !mb-[16px]"
        />

        <Formik
          initialValues={drillInitialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, setFieldTouched, isSubmitting, isValid }) => (
            <Form className="flex flex-col gap-4">
              <StepSection>
                <div className="flex items-center gap-[24px] rounded-md border border-solid border-gray-400 bg-white p-[16px]">
                  {drill?.imageUrl ? (
                    <img
                      src={drill.imageUrl}
                      alt={drill.name}
                      className="h-[78px] w-[59px] shrink-0 rounded-sm object-cover"
                    />
                  ) : (
                    <div className="h-[78px] w-[59px] shrink-0 rounded-sm bg-gray-300" />
                  )}
                  <div className="flex flex-1 flex-col gap-[10px]">
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-normal">教材名</label>
                      <Field
                        name="drillName"
                        component={Input}
                        placeholder="教材名を入力"
                      />
                      <FieldError name="drillName" />
                    </div>
                    <p className="text-[14px]">
                      セクション：{drill?.sections.length ?? 0}
                    </p>
                  </div>
                </div>

                <div className="mt-[20px] flex flex-col gap-1">
                  <p className="text-[14px]">
                    1. 更新するデータの入力テンプレートをダウンロード
                  </p>
                  <p className="text-[13px]">
                    ※テンプレートには追加分のデータのみ入力してください。すでに登録済みのデータを含める必要はありません。
                  </p>
                </div>
                <div className="mt-[20px]">
                  <button
                    type="button"
                    className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound px-[32px]"
                    disabled={isDownloading}
                    onClick={downloadTemplate}
                  >
                    CSVダウンロード
                  </button>
                </div>
              </StepSection>

              <StepSection>
                <div className="flex flex-col gap-1">
                  <p className="text-[14px]">
                    2.
                    情報を保存したCSVファイルとコンテンツ（画像・音声・PDFなど）のZIPファイルをアップロード
                  </p>
                  <p className="text-[13px] text-black">
                    ※エクセル形式ではインポートできませんので、必ずCSV形式で保存してください。
                  </p>
                </div>
                <FileUploadFields
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />
              </StepSection>

              <div className="mt-[16px] flex justify-end">
                <button
                  type="submit"
                  disabled={isPending || isSubmitting || !isValid}
                  className={classNames(
                    "buiButtonBase buiButtonVariantFilled buiButtonSizeMd py-[12px]",
                    {
                      "opacity-50": isPending || isSubmitting || !isValid,
                    },
                  )}
                >
                  {isPending || isSubmitting ? <ButtonSpinner /> : "更新"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </ContentBox>
    </div>
  );
};
